import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Api from "../../services/Api";
import {openMeeting} from "../../helpers/open_meeting";
import ChevronRightIcon from "../../components/Icons/ChevronRightIcon";
import { CameraIcon, MicrophoneIcon } from "../../components/Icons/VideoIcons";
import EmailIcon from "../../components/Icons/EmailIcon";

interface TeamMember {
  clientId: string;
  name: string;
  manager: boolean;
  visionStatements: number;
  visionStatementsShared: number;
}

interface TeamInterface {
  id: string;
  parentDepartment?: string;
  parentDepartmentManager?: string;
  name: string;
  manager: boolean;
  members: Array<TeamMember>;
}

interface FeedbackSummary {
  completed: number;
  pending: number;
  failed: number;
}

interface RespondentsWaiting {
  email: string;
  created: string;
}

interface FeedbackInterface {
  email: string;
  created: string;
  status: string;
}


const Team: React.FC = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [team, setTeam] = useState<TeamInterface | null>(null);
  const [recordings, setRecordings] = useState(0);
  const [respondentsWaiting, setRespondentsWaiting] = useState<RespondentsWaiting[]>([]);
  const [feedbacks, setFeedbacks] = useState<FeedbackInterface[]>([]);
  const [feedbackSummary, setFeedbackSummary] = useState<FeedbackSummary | null>(null);
  const [showFeedbacks, setShowFeedbacks] = useState(false);

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    api
      .get("client/team")
      .then((response) => {
        if (response.json.error) {
          setError(response.json.error);
        } else {
          setTeam(response.json.team);
          setRecordings(response.json.recordings);
          setRespondentsWaiting(response.json.respondentsWaiting);
          setFeedbackSummary(response.json.feedbackSummary);
          setFeedbacks(response.json.feedbacks);
          setLoaded(true);
        }
      })
      .catch((error) => {
        alert("Unknown error (http status " + error.response.status + ")");
      });
  };

  if (error.length > 0) {
    return <div className="mt-4">Error: {error}</div>;
  }

  if (!loaded) {
    return <div className="mt-4">Loading...</div>;
  }

  if (!team) {
    return <div className="mt-4">No team!?</div>;
  }

  return (
    <>

      <div className="mt-4 z-heading1">
        You are a {team.manager ? "manager for" : "member of"} {team.name}
      </div>

      <div className="mt-2">
        {
          team.parentDepartment ? (
            <div>
              Your team is organized under {team.parentDepartment}.
              {
                team.parentDepartmentManager &&
                <p className="mt-2">Your manager is {team.parentDepartmentManager}.</p>
              }
            </div>
          ) : (
            <p>Your team is at top level</p>
          )

        }
      </div>

      <div className="flex flex-wrap gap-4 mt-6">
        <div className="">
          <p className="font-medium text-gray-400">
            Team members
          </p>
          <div className="main-half-col bg-indigo-900 px-2 py-2 mt-2 rounded-md">

            {
              team.members.map((member, i) => (
                <div key={"member_" + i}
                     className="my-2 px-2 rounded-md cursor-pointer hover:bg-indigo-800"
                     onClick={() => navigate('/team/member/' + member.clientId)}>
                  <p className="z-title1">{member.name} {member.manager && " (manager)"}</p>
                  <p className="z-body2">
                    {member.visionStatements} Goals
                    {
                      team.manager &&
                        <>
                          , {member.visionStatementsShared} shared with you
                        </>
                    }
                  </p>

                </div>
              ))
            }
          </div>

          <div className="main-half-col bg-indigo-900 p-4 mt-4 rounded-md">
            <div className="flex items-center gap-x-4">
              <EmailIcon alt="camera" className="h-6 w-6"/>
              <div className="z-title2">Feedback requests</div>
            </div>

            <div className="mt-2 z-body1">
              {feedbackSummary?.completed} completed
            </div>
            <div className="z-body1">
              {feedbackSummary?.pending} pending
            </div>
            <div className="z-body1">
              {feedbackSummary?.failed} failed
            </div>
            <div className="z-body1">
              {respondentsWaiting.length} recipients not answered
            </div>

            <div className="z-body1 mt-2 z-link" onClick={() => setShowFeedbacks(!showFeedbacks)}>
              {showFeedbacks ? "Hide" : "Show all feedbacks"}
            </div>

            {
              showFeedbacks &&
                <>
                    <div className="mt-4 flex justify-between">
                        <p className="z-body2">Sender</p>
                        <p className="z-body2">Status</p>
                        <p className="z-body2">Created</p>
                    </div>

                  {
                    feedbacks.map((rw, i) => (
                      <div key={"feedback_" + i} className="mt-2 flex justify-between">
                        <p className="z-body2">{rw.email}</p>
                        <p className="z-body2">{rw.status}</p>
                        <p className="z-body2">{rw.created.substring(0, 10)}</p>
                      </div>
                    ))
                  }

                  {
                    respondentsWaiting.length > 0 &&
                      <div className="z-title3 mt-4">
                        {respondentsWaiting.length} recipients not answered:
                      </div>
                  }

                  {
                    respondentsWaiting.map((rw, i) => (
                      <div key={"rw_" + i} className="mt-2">
                        <p className="z-body2">{rw.email} (Received {rw.created.substring(0, 10)})</p>
                      </div>
                    ))
                  }
                </>
            }

          </div>
        </div>

        <div className="main-half-col">
          <p className="font-medium text-gray-400">
          Team tools
          </p>


          <div className="bg-indigo-900 p-4 mt-2 rounded-md hover:bg-indigo-800 cursor-pointer"
               onClick={() => navigate("/team/recordings")}>
            <div className="flex items-center gap-x-4">
              <MicrophoneIcon alt="camera" className="h-6 w-6"/>
              <div className="z-title2">Recordings</div>
            </div>
            <div className=" mt-2 flex justify-between items-center">
              <div className="z-body1">You have {recordings} recordings</div>
              <ChevronRightIcon className="h-6 w-6 text-white!" aria-hidden="true"/>
            </div>
          </div>

          <div className="bg-indigo-900 p-4 mt-4 rounded-md hover:bg-indigo-800 cursor-pointer">
            <div className="flex items-center gap-x-4 cursor-pointer"
                 onClick={() => openMeeting("team_" + team.id, api.apiToken ? api.apiToken : "", "client")}>
              <CameraIcon alt="camera" className="h-6 w-6"/>
              <div className="z-title2">Team meeting</div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Team;
